import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  GripVertical,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react';
import { FinalizedMealPlanData, FinalizedMeal } from '@/types/meal-plan';
import { AlternateRecipe, AlternateRecipeResponse } from '@/types/alternate-recipe';
import { getAlternateMacroValue } from '@/utils/alternate-recipe-utils';
import { formatMacro } from '@/utils/gram-ml';
import { DEFAULT_RECIPE_IMAGE } from '@/constants/media';
import { SmartEditMealContext } from './SmartEditModal';
import CopyDayModal from '@/components/meal-planning/manual/CopyDayModal';
import { MealPlanNotesControl } from '@/components/meal-planning/MealPlanNotesControl';
import type { UpdateMealPlanNotesRequest } from '@/lib/api/mealPlans';
import { RecipeAllergenCard } from '@/components/recipes/RecipeAllergenCard';
import { Tag } from '@/components/ui/tag';

import { getDietaryDisplayName } from '@/data/dietary-mapping';
import { AI_MEAL_PLAN_MAX_DAYS } from '@/components/meal-planning/MealPlanScheduleFields';

type SwappingMealState = { day: number; meal?: string; mealOrder?: number } | null;

const DAYS_PER_PAGE = 7;

type SortableMealSectionProps = {
  id: string;
  disabled: boolean;
  children: (dragHandleProps: {
    attributes: ReturnType<typeof useSortable>['attributes'];
    listeners: ReturnType<typeof useSortable>['listeners'];
    isDragging: boolean;
  }) => React.ReactNode;
};

const SortableMealSection: React.FC<SortableMealSectionProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : 1,
        position: 'relative',
        zIndex: isDragging ? 2 : 1,
      }}
    >
      {children({ attributes, listeners, isDragging })}
    </div>
  );
};

type MealPlanDaysTabsProps = {
  days: FinalizedMealPlanData['days'];
  activeDayTab: string;
  onActiveDayTabChange: (value: string) => void;
  smartEditDisabled?: boolean;
  regenerateDisabled?: boolean;
  smartEditLimitReached?: boolean;
  smartEditLimit?: number;
  smartEditRetryAt?: string | null;
  planId?: string;
  onDayNameUpdate?: (day: number, dayName: string | null) => Promise<void>;
  nutritionSummaryGoals?:
    | {
        calories?: number;
        proteinMin?: number;
        proteinMax?: number;
        carbsMin?: number;
        carbsMax?: number;
        fatMin?: number;
        fatMax?: number;
        fiber?: number;
      }
    | undefined;
  getMealKey: (day: number, mealName: string) => string;
  alternateRecipesMap: Record<string, AlternateRecipeResponse>;
  onViewDetails: (
    recipeId: string,
    recipeName: string,
    day: number,
    mealName: string,
    source: string | undefined,
    mealOrder: number
  ) => void;
  onSwapRecipe: (
    day: number,
    mealName: string,
    mealOrder: number,
    recipeId?: string
  ) => void;
  swappingMeal: SwappingMealState;
  onSelectAlternateRecipe: (
    day: number,
    mealName: string,
    mealOrder: number,
    newRecipe: AlternateRecipe
  ) => Promise<void> | void;
  onClearMealSelection: (
    day: number,
    mealName: string,
    mealOrder: number
  ) => Promise<void> | void;
  onOpenSmartEdit: (payload: SmartEditMealContext) => void;
  onCopyExistingDays?: (sourceDay: number, targetDays: number[]) => Promise<void> | void;
  onDuplicateToNextDay?: (sourceDay: number) => Promise<void> | void;
  onAddCopiedDay?: (sourceDay: number) => Promise<void> | void;
  onApplyToAllDays?: (sourceDay: number) => Promise<void> | void;
  onReorderMeals?: (day: number, orderedMealOrders: number[]) => Promise<void> | void;
  preferredChoicesSummary?: React.ReactNode;
  /** Practitioner notes — same plan/day/meal controls as manual board. */
  planNotes?: string | null;
  canEditNotes?: boolean;
  isSavingNotes?: boolean;
  onSaveNotes?: (request: UpdateMealPlanNotesRequest) => Promise<boolean>;
};

type SuggestionCard = {
  id: string;
  title: string;
  image: string;
  description: string;
  source?: string;
  itemId?: string | null;
  itemType?: 'recipe' | 'ingredient' | string | null;
  optionGroupId?: string | null;
  optionGroupLabel?: string | null;
  selectedOptionGroupId?: string | null;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  isSelected: boolean;
  alternate?: AlternateRecipe;
  dietLabels?: string[];
  cuisines?: string[];
  cuisineTypes?: string[];
  allergens?: string[];
  generatedVia?: 'shuffle' | 'smart_edit' | string | null;
  generationIndicator?: AlternateRecipe['generationIndicator'];
  generatedFromTitle?: string | null;
};

type GenericMealChoiceGroup = {
  optionGroupId: string;
  label: string;
  suggestions: SuggestionCard[];
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
};

const parseMacro = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const cleanString = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : String(value ?? '').trim()
);

const getVisibleRecipeTitle = (value: unknown, fallback: string): string => {
  const title = cleanString(value);
  if (!title) return fallback;

  const legacyFallbackSuffix = /\s*\(Safe Option \d+(?:-\d+){2}\)\s*$/i;
  if (!legacyFallbackSuffix.test(title)) return title;

  return title
    .replace(legacyFallbackSuffix, '')
    .replace(/^(?:World\s+)+/i, '')
    .trim() || fallback;
};

const safeDateFormat = (value: string | undefined, dateFormat: string, fallback = '') => {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;
  return format(parsed, dateFormat);
};

const humanMealName = (value: string) => {
  const cleaned = value.replace(/_\d+$/, '').replace(/[-_]+/g, ' ').trim();
  if (!cleaned) return 'Meal';
  return cleaned
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getMealOrder = (meal: FinalizedMeal, fallbackOrder: number) => {
  const raw = Number(meal.mealOrder ?? meal.slotOrder ?? fallbackOrder);
  return Number.isFinite(raw) && raw > 0 ? raw : fallbackOrder;
};

const normalizeSourceLabel = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const cleaned = value.trim();
  return cleaned.length > 0 ? cleaned : undefined;
};

const isInternalDisplayValue = (value: string): boolean => (
  value.trim().toLowerCase().replace(/[-\s]+/g, '_') === 'internal'
);

const getVisibleRecipeSourceLabel = (value: unknown): string | undefined => {
  const source = normalizeSourceLabel(value);
  if (!source) return undefined;
  return isInternalDisplayValue(source) ? undefined : source;
};

const normalizeTagValues = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      if (entry && typeof entry === 'object') {
        const record = entry as Record<string, unknown>;
        return String(record.value ?? record.id ?? record.name ?? record.label ?? '').trim();
      }
      return String(entry ?? '').trim();
    })
    .filter((entry) => entry.length > 0 && !isInternalDisplayValue(entry));
};

const mergeTagValues = (...values: unknown[]): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];

  values.forEach((value) => {
    normalizeTagValues(value).forEach((entry) => {
      const key = entry.toLowerCase().replace(/[-\s]+/g, '_');
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(entry);
    });
  });

  return merged;
};

const firstNonEmptyTags = (...values: unknown[]): string[] => {
  for (const value of values) {
    const normalized = mergeTagValues(value);
    if (normalized.length > 0) return normalized;
  }
  return [];
};

const resolveUpdatedSuggestionAllergens = (
  card: SuggestionCard,
  existing?: SuggestionCard
): string[] | undefined => {
  if (Array.isArray(card.allergens)) {
    return mergeTagValues(card.allergens);
  }
  return existing?.allergens;
};

const getMealAllergens = (meal: FinalizedMeal): string[] => {
  const record = meal as FinalizedMeal & {
    allergenTagIds?: unknown;
    allergyTags?: unknown;
    cautions?: unknown;
  };
  return mergeTagValues(
    meal.allergens,
    record.allergenTagIds,
    record.allergyTags,
    record.cautions
  );
};

const getAlternateAllergens = (recipe: AlternateRecipe): string[] => {
  const record = recipe as AlternateRecipe & {
    allergenTagIds?: unknown;
    allergyTags?: unknown;
    cautions?: unknown;
  };
  return mergeTagValues(
    recipe.allergens,
    record.allergenTagIds,
    record.allergyTags,
    record.cautions
  );
};

const getGeneratedIndicatorLabel = (
  value: Pick<SuggestionCard, 'generatedVia' | 'generationIndicator'> | AlternateRecipe | null | undefined
): string | null => {
  const explicitLabel = typeof value?.generationIndicator?.label === 'string'
    ? value.generationIndicator.label.trim()
    : '';
  if (explicitLabel) return explicitLabel;

  const generatedVia = typeof value?.generatedVia === 'string'
    ? value.generatedVia.trim().toLowerCase()
    : '';
  if (generatedVia === 'smart_edit') return 'Smart Edit';
  if (generatedVia === 'shuffle' || generatedVia === 'regenerate') return 'Regenerated';
  return null;
};

const toAlternateRecipeFromMealOption = (meal: FinalizedMeal): AlternateRecipe | undefined => {
  const recipeId = cleanString(meal.recipeId);
  const itemId = cleanString(meal.itemId);
  const resolvedId = recipeId || itemId;
  if (!resolvedId) return undefined;

  const macros = (meal.nutrition?.macros ?? meal.nutrition ?? {}) as Record<string, unknown>;
  const calories = parseMacro(macros.calories);
  const protein = parseMacro(macros.protein);
  const carbs = parseMacro(macros.carbs ?? macros.carbohydrate);
  const fat = parseMacro(macros.fat ?? macros.total_fat);
  const fiber = parseMacro(macros.fiber ?? macros.dietary_fibre);
  const optionGroupId = cleanString(meal.optionGroupId);
  const optionGroupLabel = cleanString(meal.optionGroupLabel);
  const selectedOptionGroupId = cleanString(meal.selectedOptionGroupId);

  return {
    id: resolvedId,
    title: getVisibleRecipeTitle(meal.recipeName || meal.name, 'Meal Item'),
    image: meal.recipeImage || DEFAULT_RECIPE_IMAGE,
    itemId: itemId || resolvedId,
    itemType: meal.itemType ?? (recipeId ? 'recipe' : 'ingredient'),
    optionGroupId: optionGroupId || null,
    optionGroupLabel: optionGroupLabel || null,
    selectedOptionGroupId: selectedOptionGroupId || null,
    source: normalizeSourceLabel(meal.recipeSource),
    calories,
    protein,
    carbs,
    fat,
    fiber,
    nutrition: {
      calories: { quantity: calories },
      macros: {
        protein: { quantity: protein, unit: 'g' },
        carbs: { quantity: carbs, unit: 'g' },
        fat: { quantity: fat, unit: 'g' },
        fiber: { quantity: fiber, unit: 'g' },
      },
    },
    dietLabels: meal.dietLabels ?? meal.diets ?? [],
    cuisines: meal.cuisines ?? meal.cuisineTypes ?? [],
    cuisineTypes: meal.cuisineTypes ?? meal.cuisines ?? [],
    allergens: getMealAllergens(meal),
    servings: meal.nutritionServings ?? meal.servings,
    readyInMinutes: null,
    generatedVia: meal.generatedVia,
    generatedFromRecipeId: meal.generatedFromRecipeId,
    generatedFromSourceId: meal.generatedFromSourceId,
    generatedFromTitle: meal.generatedFromTitle,
    generationGroupId: meal.generationGroupId,
    generatedAt: meal.generatedAt,
    generationIndicator: meal.generationIndicator,
  };
};

const buildBaseSuggestion = (meal: FinalizedMeal, mealName: string): SuggestionCard => {
  const macros = (meal.nutrition?.macros ?? meal.nutrition ?? {}) as Record<string, unknown>;
  const recipeId = cleanString(meal.recipeId);
  const itemId = cleanString(meal.itemId);
  const optionGroupId = cleanString(meal.optionGroupId);
  const optionGroupLabel = cleanString(meal.optionGroupLabel);
  const selectedOptionGroupId = cleanString(meal.selectedOptionGroupId);

  return {
    id: recipeId || itemId || `${mealName}-selected`,
    title: getVisibleRecipeTitle(meal.recipeName, `${humanMealName(mealName)} Option`),
    image: meal.recipeImage || DEFAULT_RECIPE_IMAGE,
    description:
      'High quality, balanced meal selected based on client profile, nutrition goals, and dietary constraints.',
    source: normalizeSourceLabel(meal.recipeSource),
    itemId: itemId || recipeId || null,
    itemType: meal.itemType ?? (recipeId ? 'recipe' : 'ingredient'),
    optionGroupId: optionGroupId || null,
    optionGroupLabel: optionGroupLabel || null,
    selectedOptionGroupId: selectedOptionGroupId || null,
    calories: parseMacro(macros.calories),
    protein: parseMacro(macros.protein),
    carbs: parseMacro(macros.carbs ?? macros.carbohydrate),
    fat: parseMacro(macros.fat ?? macros.total_fat),
    fiber: parseMacro(macros.fiber ?? macros.dietary_fibre),
    isSelected: true,
    dietLabels: meal.dietLabels ?? meal.diets ?? [],
    cuisines: meal.cuisines ?? meal.cuisineTypes ?? [],
    cuisineTypes: meal.cuisineTypes ?? meal.cuisines ?? [],
    allergens: getMealAllergens(meal),
    generatedVia: meal.generatedVia,
    generationIndicator: meal.generationIndicator,
    generatedFromTitle: meal.generatedFromTitle,
  };
};

const buildSuggestionFromMealOption = (
  mealOption: FinalizedMeal,
  mealName: string,
  selectedRecipeId: string,
  selectedOptionGroupId: string
): SuggestionCard => {
  const suggestion = buildBaseSuggestion(mealOption, mealName);
  const optionRecipeId = cleanString(mealOption.recipeId);
  const optionGroupId = cleanString(mealOption.optionGroupId);
  const isSelected = optionGroupId
    ? optionGroupId === selectedOptionGroupId
    : optionRecipeId.length > 0 && optionRecipeId === selectedRecipeId;

  return {
    ...suggestion,
    id: optionRecipeId || cleanString(mealOption.itemId) || suggestion.id,
    description: isSelected
      ? suggestion.description
      : 'AI-generated alternative aligned to this slot and client constraints. Select to replace current choice.',
    isSelected,
    alternate: toAlternateRecipeFromMealOption(mealOption),
    generatedVia: mealOption.generatedVia,
    generationIndicator: mealOption.generationIndicator,
    generatedFromTitle: mealOption.generatedFromTitle,
  };
};

const buildAlternateSuggestion = (recipe: AlternateRecipe): SuggestionCard => ({
  id: recipe.id,
  title: getVisibleRecipeTitle(recipe.title, 'Recipe Suggestion'),
  image: recipe.image || DEFAULT_RECIPE_IMAGE,
  description:
    'AI-generated alternative aligned to this slot and client constraints. Select to replace current choice.',
  source: normalizeSourceLabel(recipe.source),
  itemId: recipe.itemId ?? recipe.id,
  itemType: recipe.itemType ?? 'recipe',
  optionGroupId: recipe.optionGroupId ?? null,
  optionGroupLabel: recipe.optionGroupLabel ?? null,
  selectedOptionGroupId: recipe.selectedOptionGroupId ?? null,
  calories: parseMacro(getAlternateMacroValue(recipe, 'calories')),
  protein: parseMacro(getAlternateMacroValue(recipe, 'protein')),
  carbs: parseMacro(getAlternateMacroValue(recipe, 'carbs')),
  fat: parseMacro(getAlternateMacroValue(recipe, 'fat')),
  fiber: parseMacro(getAlternateMacroValue(recipe, 'fiber')),
  isSelected: false,
  alternate: recipe,
  dietLabels: recipe.dietLabels ?? [],
  cuisines: recipe.cuisines ?? recipe.cuisineTypes ?? [],
  cuisineTypes: recipe.cuisineTypes ?? recipe.cuisines ?? [],
  allergens: getAlternateAllergens(recipe),
  generatedVia: recipe.generatedVia,
  generationIndicator: recipe.generationIndicator,
  generatedFromTitle: recipe.generatedFromTitle,
});

const SuggestionPreferenceDetails: React.FC<Pick<SuggestionCard, 'cuisines' | 'cuisineTypes' | 'allergens'>> = ({
  cuisines,
  cuisineTypes,
  allergens,
}) => {
  const displayCuisines = firstNonEmptyTags(cuisines, cuisineTypes).filter((cuisine) => {
    const token = cuisine.toLowerCase().replace(/[^a-z0-9]+/g, '');
    return token !== 'world' && token !== 'worldnopreference' && token !== 'nopreference';
  });
  const displayAllergens = firstNonEmptyTags(allergens);
  const hasCuisines = displayCuisines.length > 0;
  const hasAllergens = displayAllergens.length > 0;

  if (!hasCuisines && !hasAllergens) return null;

  return (
    <div className="mt-2 space-y-1.5 py-2">
      {hasCuisines ? (
        <div className="flex items-start gap-2 text-xs leading-snug text-slate-600">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-500" aria-hidden="true" />
          <p className="min-w-0">
            <span className="font-semibold text-slate-700">Cuisine: </span>
            <span className="font-medium text-slate-600">
              {displayCuisines
                .slice(0, 2)
                .map((cuisine) => getDietaryDisplayName(cuisine, 'cuisineTypes'))
                .join(', ')}
            </span>
          </p>
        </div>
      ) : null}

      {hasAllergens ? (
        <div className="flex items-start gap-2">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" aria-hidden="true" />
          <RecipeAllergenCard
            allergens={displayAllergens}
            compact
            maxVisible={3}
            className="min-w-0 flex-1"
          />
        </div>
      ) : null}
    </div>
  );
};

const buildGenericMealChoiceLabel = (group: SuggestionCard[], index: number): string => {
  const explicit = group
    .map((suggestion) => cleanString(suggestion.optionGroupLabel))
    .find(Boolean);
  return explicit || `Meal choice ${index + 1}`;
};

const buildGenericMealChoiceGroups = (suggestions: SuggestionCard[]): GenericMealChoiceGroup[] => {
  const groups: GenericMealChoiceGroup[] = [];
  const indexByGroupId = new Map<string, number>();

  suggestions.forEach((suggestion) => {
    const optionGroupId = cleanString(suggestion.optionGroupId);
    if (!optionGroupId) return;

    const existingIndex = indexByGroupId.get(optionGroupId);
    if (existingIndex !== undefined) {
      groups[existingIndex].suggestions.push(suggestion);
      groups[existingIndex].calories += suggestion.calories;
      groups[existingIndex].protein += suggestion.protein;
      groups[existingIndex].carbs += suggestion.carbs;
      groups[existingIndex].fat += suggestion.fat;
      return;
    }

    const nextGroup: GenericMealChoiceGroup = {
      optionGroupId,
      label: '',
      suggestions: [suggestion],
      calories: suggestion.calories,
      protein: suggestion.protein,
      carbs: suggestion.carbs,
      fat: suggestion.fat,
    };
    groups.push(nextGroup);
    indexByGroupId.set(optionGroupId, groups.length - 1);
  });

  groups.forEach((group, index) => {
    group.label = buildGenericMealChoiceLabel(group.suggestions, index);
  });

  return groups;
};

const getGenericChoicePreviewText = (group: GenericMealChoiceGroup): string => (
  group.suggestions
    .map((suggestion) => suggestion.title)
    .filter(Boolean)
    .slice(0, 3)
    .join(' + ')
);

const MealPlanDaysTabs: React.FC<MealPlanDaysTabsProps> = ({
  days,
  activeDayTab,
  onActiveDayTabChange,
  smartEditDisabled = false,
  regenerateDisabled = false,
  smartEditLimitReached = false,
  smartEditLimit,
  smartEditRetryAt,
  getMealKey,
  alternateRecipesMap,
  onViewDetails,
  onSwapRecipe,
  swappingMeal,
  onSelectAlternateRecipe,
  onClearMealSelection,
  onOpenSmartEdit,
  onCopyExistingDays,
  onDuplicateToNextDay,
  onAddCopiedDay,
  onApplyToAllDays,
  onReorderMeals,
  preferredChoicesSummary,
  planNotes,
  canEditNotes = false,
  isSavingNotes = false,
  onSaveNotes,
}) => {
  const [dayPage, setDayPage] = useState(0);
  const smartEditRetryAtLabel = useMemo(() => {
    if (!smartEditRetryAt) return null;
    const parsed = new Date(smartEditRetryAt);
    if (Number.isNaN(parsed.getTime())) return smartEditRetryAt;
    return parsed.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, [smartEditRetryAt]);
  const [selectingKey, setSelectingKey] = useState<string | null>(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyingExisting, setCopyingExisting] = useState(false);
  const [duplicatingToNext, setDuplicatingToNext] = useState(false);
  const [addingCopiedDay, setAddingCopiedDay] = useState(false);
  const [reorderingMeals, setReorderingMeals] = useState(false);
  const [applyAllDialogOpen, setApplyAllDialogOpen] = useState(false);
  const [applyingToAllDays, setApplyingToAllDays] = useState(false);
  const sortedDays = useMemo(
    () => [...(days || [])].sort((left, right) => Number(left.day) - Number(right.day)),
    [days]
  );

  const activeDay = useMemo(() => {
    if (!sortedDays.length) return null;
    return sortedDays.find((day) => String(day.day) === activeDayTab) ?? sortedDays[0];
  }, [sortedDays, activeDayTab]);

  const activeDayIndex = useMemo(() => {
    if (!activeDay) return 0;
    return Math.max(0, sortedDays.findIndex((day) => day.day === activeDay.day));
  }, [activeDay, sortedDays]);

  const mealEntries = useMemo(() => {
    if (!activeDay) return [];
    return Object.entries(activeDay.meals || {})
      .map(([mealName, meal], index) => ({ mealName, meal, index }))
      .filter(({ meal }) => Boolean(meal))
      .sort((left, right) => {
        const leftOrder = getMealOrder(left.meal, left.index + 1);
        const rightOrder = getMealOrder(right.meal, right.index + 1);
        return leftOrder - rightOrder;
      });
  }, [activeDay]);

  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const totalPages = Math.max(1, Math.ceil(sortedDays.length / DAYS_PER_PAGE));

  useEffect(() => {
    if (!sortedDays.length) return;
    if (!sortedDays.some((day) => String(day.day) === activeDayTab)) {
      onActiveDayTabChange(String(sortedDays[0].day));
    }
  }, [activeDayTab, onActiveDayTabChange, sortedDays]);

  useEffect(() => {
    setDayPage(Math.floor(activeDayIndex / DAYS_PER_PAGE));
  }, [activeDayIndex]);

  if (!sortedDays.length || !activeDay) {
    return null;
  }

  const visibleDays = sortedDays.slice(dayPage * DAYS_PER_PAGE, dayPage * DAYS_PER_PAGE + DAYS_PER_PAGE);

  const goToRelativeDay = (delta: number) => {
    const targetIndex = activeDayIndex + delta;
    if (targetIndex < 0 || targetIndex >= sortedDays.length) return;
    onActiveDayTabChange(String(sortedDays[targetIndex].day));
  };

  const handleApplyToAllDays = async () => {
    if (!onApplyToAllDays) {
      setApplyAllDialogOpen(false);
      return;
    }

    setApplyingToAllDays(true);
    try {
      await onApplyToAllDays(activeDay.day);
      setApplyAllDialogOpen(false);
    } catch {
      // Errors are handled by parent callbacks (toast). Keep dialog open for retry.
    } finally {
      setApplyingToAllDays(false);
    }
  };

  const handleCopyExistingDays = async (sourceDay: number, targetDays: number[]) => {
    if (!onCopyExistingDays) {
      setCopyModalOpen(false);
      return;
    }

    setCopyingExisting(true);
    try {
      await onCopyExistingDays(sourceDay, targetDays);
      setCopyModalOpen(false);
    } catch {
      // Parent callbacks handle errors.
    } finally {
      setCopyingExisting(false);
    }
  };

  const handleDuplicateToNextDay = async () => {
    if (!onDuplicateToNextDay || !hasNextDay) return;
    setDuplicatingToNext(true);
    try {
      await onDuplicateToNextDay(activeDay.day);
    } finally {
      setDuplicatingToNext(false);
    }
  };

  const handleAddCopiedDay = async () => {
    if (!onAddCopiedDay || sortedDays.length >= AI_MEAL_PLAN_MAX_DAYS) return;
    setAddingCopiedDay(true);
    try {
      await onAddCopiedDay(activeDay.day);
    } finally {
      setAddingCopiedDay(false);
    }
  };

  const mealDragId = (mealOrder: number) => `ai-meal-slot-${mealOrder}`;

  const handleMealDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!onReorderMeals || !over || active.id === over.id || reorderingMeals) return;
    const activeIndex = mealEntries.findIndex(
      ({ meal, index }) => mealDragId(getMealOrder(meal, index + 1)) === active.id
    );
    const overIndex = mealEntries.findIndex(
      ({ meal, index }) => mealDragId(getMealOrder(meal, index + 1)) === over.id
    );
    if (activeIndex < 0 || overIndex < 0) return;

    const orderedMealOrders = arrayMove(mealEntries, activeIndex, overIndex).map(
      ({ meal, index }) => getMealOrder(meal, index + 1)
    );
    setReorderingMeals(true);
    try {
      await onReorderMeals(activeDay.day, orderedMealOrders);
    } finally {
      setReorderingMeals(false);
    }
  };

  const hasPreviousDay = activeDayIndex > 0;
  const hasNextDay = activeDayIndex < sortedDays.length - 1;
  const hasOtherDays = sortedDays.length > 1;
  const canAddDay = sortedDays.length < AI_MEAL_PLAN_MAX_DAYS;
  const availableDaysForCopy = sortedDays
    .map((day) => Number(day.day))
    .filter((day): day is number => Number.isFinite(day));

  const topActionButtonBaseClass =
    'h-12 min-w-[136px] rounded-[12px] justify-center px-3.5 text-[14px] font-semibold';
  const topActionOutlineClass = `${topActionButtonBaseClass} h-10 min-w-[142px] px-[15px] text-[14px] border-2 border-slate-300 bg-white text-slate-900 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:border-slate-300 disabled:bg-white disabled:text-slate-500 disabled:opacity-100`;
  const topActionPrimaryClass = `${topActionButtonBaseClass} min-w-[154px] bg-cyan-500 text-white shadow-[0_5px_10px_rgba(8,145,178,0.2)] hover:bg-cyan-600 hover:text-white`;

  return (
    <div className="space-y-4">
      {(canEditNotes || planNotes?.trim()) && onSaveNotes ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <MealPlanNotesControl
            value={planNotes}
            disabled={isSavingNotes}
            editable={canEditNotes}
            variant="icon"
            iconLabel="Plan note"
            size="sm"
            label="Meal plan note"
            emptyLabel="Add a note for this meal plan"
            helperText="Optional tip or reminder. This can also show on the PDF export."
            placeholder="Enter meal plan notes"
            onSave={async (notes) => onSaveNotes({ level: 'plan', notes })}
          />
        </div>
      ) : null}
      <Card className="rounded-[20px] border-[3px] border-slate-300 shadow-none">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2">
              {hasPreviousDay && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-md border border-slate-300"
                  onClick={() => goToRelativeDay(-1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}
              <div>
                <p className="text-[24px] font-semibold leading-none text-slate-800">Day {activeDay.day}</p>
                <p className="mt-1 text-xs font-semibold text-slate-600">
                  {safeDateFormat(activeDay.date, 'EEE, MMM d', activeDay.date)}
                </p>
              </div>
              {(canEditNotes || activeDay.notes?.trim()) && onSaveNotes ? (
                <MealPlanNotesControl
                  value={activeDay.notes}
                  disabled={isSavingNotes}
                  editable={canEditNotes}
                  variant="icon"
                  iconLabel="Day note"
                  size="sm"
                  label="Day note"
                  emptyLabel="Add a note for this day"
                  helperText="Optional. Add guidance for this day only."
                  placeholder="Enter day notes"
                  onSave={async (notes) =>
                    onSaveNotes({ level: 'day', day: activeDay.day, notes })
                  }
                />
              ) : null}
              {hasNextDay && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-md border border-slate-300"
                  onClick={() => goToRelativeDay(1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
              <span className="ml-1 text-xs text-slate-500">Week {dayPage + 1} of {totalPages}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={topActionOutlineClass}
                onClick={() => setCopyModalOpen(true)}
                disabled={!hasOtherDays || copyingExisting}
              >
                {copyingExisting ? 'Copying...' : 'Copy Existing'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={topActionOutlineClass}
                onClick={() => {
                  void handleDuplicateToNextDay();
                }}
                disabled={!hasNextDay || duplicatingToNext}
              >
                <Copy className="mr-1.5 h-4 w-4" />
                {duplicatingToNext ? 'Duplicating...' : 'Duplicate to Next'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={topActionOutlineClass}
                onClick={() => {
                  void handleAddCopiedDay();
                }}
                disabled={!onAddCopiedDay || !canAddDay || addingCopiedDay}
                title={
                  canAddDay
                    ? `Copy Day ${activeDay.day} into a new Day ${sortedDays.length + 1}`
                    : `AI meal plans support up to ${AI_MEAL_PLAN_MAX_DAYS} days`
                }
              >
                <Plus className="mr-1.5 h-4 w-4" />
                {addingCopiedDay ? 'Adding...' : 'Add Day'}
              </Button>
              <Button
                type="button"
                size="sm"
                className={topActionPrimaryClass}
                onClick={() => setApplyAllDialogOpen(true)}
              >
                Apply to All Days
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7">
            {visibleDays.map((day) => {
              const isActive = day.day === activeDay.day;
              const mealsCount = Object.keys(day.meals || {}).length;
              return (
                <button
                  key={day.day}
                  type="button"
                  onClick={() => onActiveDayTabChange(String(day.day))}
                  className={`flex min-h-[112px] flex-col items-center justify-center rounded-xl border px-3 py-2 text-center transition-colors ${
                    isActive
                      ? 'border-2 border-[#0FA8D8] bg-[#F8FDFF]'
                      : 'border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <p className="text-xs font-extrabold uppercase tracking-wide text-slate-700">
                    {safeDateFormat(day.date, 'EEE', 'Day')}
                  </p>
                  <p className="mt-1 text-lg font-semibold leading-none text-slate-800">
                    {safeDateFormat(day.date, 'd', String(day.day))}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">Day {day.day}</p>
                  <p className="mt-2 text-xs text-[#A65F00]">{mealsCount} meals</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {preferredChoicesSummary}

      {smartEditLimitReached && (
        <section className="flex items-center gap-2.5 rounded-xl border border-amber-400 bg-white px-4 py-3">
          <ShieldAlert className="h-4 w-4 shrink-0 self-center text-amber-500" />
          <div>
            <h3 className="text-[13px] font-semibold text-slate-900">Smart Edit Limit Reached</h3>
            <p className="mt-0.5 text-xs text-slate-600">
              {typeof smartEditLimit === 'number' && smartEditLimit > 0
                ? `You've used all ${smartEditLimit} Smart Edit actions in the last 24 hours.`
                : "You've used all available Smart Edit actions in the last 24 hours."}{' '}
              {smartEditRetryAtLabel
                ? `Smart Edit is disabled until ${smartEditRetryAtLabel}.`
                : 'Smart Edit is disabled until the rolling limit resets.'}
            </p>
          </div>
        </section>
      )}

      <DndContext
        sensors={dragSensors}
        collisionDetection={closestCenter}
        onDragEnd={(event) => {
          void handleMealDragEnd(event);
        }}
      >
        <SortableContext
          items={mealEntries.map(({ meal, index }) => mealDragId(getMealOrder(meal, index + 1)))}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {mealEntries.map(({ mealName, meal, index }) => {
          const mealOrder = getMealOrder(meal, index + 1);
          const mealKey = getMealKey(activeDay.day, mealName);
          const alternates = alternateRecipesMap[mealKey]?.alternateRecipes ?? [];
          const isRegenerating = swappingMeal?.day === activeDay.day && (
            typeof swappingMeal.mealOrder === 'number'
              ? swappingMeal.mealOrder === mealOrder
              : swappingMeal.meal === mealName
          );
          const selectedRecipeId = String(meal.recipeId ?? '').trim();
          const invalidSelection = meal.invalidSelection;
          const mealSelectedOptionGroupId = cleanString(meal.selectedOptionGroupId);
          const isAnyMealRegenerating = swappingMeal !== null;
          const isRegenerateDisabled = regenerateDisabled || isAnyMealRegenerating || !Number.isFinite(mealOrder) || mealOrder < 1;
          const mealOptionSuggestions = (Array.isArray(meal.recipes) ? meal.recipes : []).map(
            (mealOption) => buildSuggestionFromMealOption(
              mealOption,
              mealName,
              selectedRecipeId,
              mealSelectedOptionGroupId
            )
          );
          const alternateSuggestionCards = alternates.map((alternate) => buildAlternateSuggestion(alternate));
          const dedupedById = new Map<string, SuggestionCard>();
          mealOptionSuggestions.forEach((card) => {
            const cardId = String(card.id ?? '').trim();
            const optionGroupId = cleanString(card.optionGroupId);
            const cardKey = `${optionGroupId || 'single'}:${cardId}`;
            if (!cardId || dedupedById.has(cardKey)) return;
            dedupedById.set(cardKey, {
              ...card,
              isSelected: optionGroupId
                ? optionGroupId === mealSelectedOptionGroupId
                : cardId === selectedRecipeId,
            });
          });
          alternateSuggestionCards.forEach((card) => {
            const cardId = String(card.id ?? '').trim();
            if (!cardId) return;
            const optionGroupId = cleanString(card.optionGroupId);
            const cardKey = `${optionGroupId || 'single'}:${cardId}`;
            const existing = dedupedById.get(cardKey);
            dedupedById.set(cardKey, {
              ...(existing ?? card),
              ...card,
              id: cardId,
              isSelected: optionGroupId
                ? optionGroupId === mealSelectedOptionGroupId
                : cardId === selectedRecipeId,
              alternate: card.alternate ?? existing?.alternate,
              dietLabels:
                Array.isArray(card.dietLabels) && card.dietLabels.length > 0
                  ? card.dietLabels
                  : existing?.dietLabels,
              cuisines:
                Array.isArray(card.cuisines) && card.cuisines.length > 0
                  ? card.cuisines
                  : existing?.cuisines ?? existing?.cuisineTypes,
              cuisineTypes:
                Array.isArray(card.cuisineTypes) && card.cuisineTypes.length > 0
                  ? card.cuisineTypes
                  : existing?.cuisineTypes ?? existing?.cuisines,
              allergens: resolveUpdatedSuggestionAllergens(card, existing),
            });
          });
          const suggestions: SuggestionCard[] = Array.from(dedupedById.values());
          const genericChoiceGroups = buildGenericMealChoiceGroups(suggestions);
          const resolvedSelectedOptionGroupId =
            mealSelectedOptionGroupId ||
            suggestions.find((suggestion) => suggestion.isSelected && cleanString(suggestion.optionGroupId))?.optionGroupId ||
            '';
          const isGenericMealSlot = genericChoiceGroups.length > 0;
          const hasSelectedMeal = isGenericMealSlot
            ? Boolean(resolvedSelectedOptionGroupId)
            : Boolean(selectedRecipeId || cleanString(meal.recipeName));
          const displaySuggestions = isGenericMealSlot
            ? resolvedSelectedOptionGroupId
              ? suggestions.filter((suggestion) => {
                  const optionGroupId = cleanString(suggestion.optionGroupId);
                  return !optionGroupId || optionGroupId === resolvedSelectedOptionGroupId;
                }).map((suggestion) => ({
                  ...suggestion,
                  isSelected: cleanString(suggestion.optionGroupId)
                    ? cleanString(suggestion.optionGroupId) === resolvedSelectedOptionGroupId
                    : suggestion.isSelected,
                }))
              : []
            : suggestions;

          return (
            <SortableMealSection
              key={mealKey}
              id={mealDragId(mealOrder)}
              disabled={!onReorderMeals || reorderingMeals}
            >
              {({ attributes, listeners, isDragging }) => (
            <Card className={`border border-slate-200 shadow-none ${isDragging ? 'ring-2 ring-cyan-300' : ''}`}>
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-[18px] font-semibold text-slate-800">
                        {humanMealName(mealName)} Suggestions
                      </h3>
                      {(canEditNotes || meal.notes?.trim()) && onSaveNotes ? (
                        <MealPlanNotesControl
                          value={meal.notes}
                          disabled={isSavingNotes}
                          editable={canEditNotes}
                          variant="icon"
                          size="sm"
                          label="Meal note"
                          emptyLabel="Add a note for this meal"
                          helperText="Optional. Tips for this meal only."
                          placeholder="Enter meal notes"
                          onSave={async (notes) =>
                            onSaveNotes({
                              level: 'meal',
                              day: activeDay.day,
                              mealName: String(meal.mealName ?? mealName).trim() || mealName,
                              notes,
                            })
                          }
                        />
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-500">
                      {isGenericMealSlot
                        ? 'Select the meal choice used for this cohort meal plan'
                        : 'Select one option to add to your plan'}
                    </p>
                  </div>
                  {(isRegenerating || !isAnyMealRegenerating) && (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      className="flex h-8 w-8 cursor-grab items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 hover:border-cyan-400 hover:text-cyan-700 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Drag to reorder ${humanMealName(mealName)} meal slot`}
                      title="Drag to reorder meal slot"
                      disabled={!onReorderMeals || reorderingMeals}
                      {...attributes}
                      {...listeners}
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                    {hasSelectedMeal ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-lg px-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        onClick={() => {
                          void onClearMealSelection(activeDay.day, mealName, mealOrder);
                        }}
                      >
                        Clear selection
                      </Button>
                    ) : null}
                    <Button
                      data-demo-id="ai-plan-draft-regenerate"
                      type="button"
                      variant="outline"
                      size="sm"
                      className={`h-8 text-xs disabled:opacity-100 ${
                        isRegenerating
                          ? 'pointer-events-none border-cyan-300 bg-cyan-50 text-cyan-700 hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 disabled:border-cyan-300 disabled:bg-cyan-50 disabled:text-cyan-700'
                          : 'border-slate-300 bg-white text-slate-900 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-600'
                      }`}
                      onClick={() => {
                        if (isRegenerating) return;
                        onSwapRecipe(
                          activeDay.day,
                          mealName,
                          mealOrder,
                          selectedRecipeId || undefined
                        );
                      }}
                      disabled={isRegenerateDisabled}
                    >
                      <RefreshCw className={`mr-1 h-3 w-3 ${isRegenerating ? 'animate-spin' : ''}`} />
                      {isRegenerating ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                  </div>
                  )}
                </div>

                {invalidSelection ? (
                  <div className="flex items-start gap-2.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3">
                    <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-amber-950">Selected recipe needs replacing</p>
                      <p className="mt-0.5 text-xs text-amber-900">
                        {invalidSelection.message || 'This recipe no longer satisfies the client’s hard constraints. Clear it or select another suggestion.'}
                      </p>
                      {invalidSelection.violations.length > 0 ? (
                        <p className="mt-1 text-xs text-amber-800">
                          {invalidSelection.violations.join(' · ')}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {isGenericMealSlot ? (
                  <div className="rounded-xl border border-cyan-100 bg-cyan-50/60 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-cyan-800">
                      Meal choice selector
                    </p>
                    <div className="mt-2 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                      {genericChoiceGroups.map((group) => {
                        const isChoiceSelected = group.optionGroupId === resolvedSelectedOptionGroupId;
                        const choiceSelectionKey = `${activeDay.day}-${mealName}-choice-${group.optionGroupId}`;
                        const isSelectingChoice = selectingKey === choiceSelectionKey;
                        const selectableSuggestion = group.suggestions.find((suggestion) => suggestion.alternate);
                        const disabled = isSelectingChoice || (!isChoiceSelected && !selectableSuggestion?.alternate);

                        const handleChoiceSelect = async () => {
                          if (disabled) return;
                          setSelectingKey(choiceSelectionKey);
                          try {
                            if (isChoiceSelected) {
                              await onClearMealSelection(activeDay.day, mealName, mealOrder);
                            } else if (selectableSuggestion?.alternate) {
                              await onSelectAlternateRecipe(
                                activeDay.day,
                                mealName,
                                mealOrder,
                                selectableSuggestion.alternate
                              );
                            }
                          } finally {
                            setSelectingKey(null);
                          }
                        };

                        return (
                          <button
                            key={group.optionGroupId}
                            type="button"
                            className={`flex min-h-[116px] flex-col rounded-[10px] border bg-white p-3 text-left transition-colors ${
                              isChoiceSelected
                                ? 'border-cyan-500 shadow-[0_0_0_1px_rgba(6,182,212,0.18)]'
                                : 'border-slate-200 hover:border-cyan-300'
                            } disabled:cursor-default disabled:opacity-100`}
                            onClick={() => {
                              void handleChoiceSelect();
                            }}
                            disabled={disabled}
                          >
                            <span className="flex items-start gap-2">
                              <input
                                type="radio"
                                className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-600"
                                name={`generic-meal-choice-${activeDay.day}-${mealName}`}
                                checked={isChoiceSelected}
                                readOnly
                              />
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-slate-900">
                                  {group.label}
                                </span>
                                <span className="mt-1 block line-clamp-2 text-xs leading-4 text-slate-500">
                                  {getGenericChoicePreviewText(group)}
                                </span>
                              </span>
                            </span>
                            <span className="mt-auto grid grid-cols-4 gap-1 pt-3 text-center text-[11px] text-slate-600">
                              <span>
                                <span className="block font-semibold text-cyan-700">{formatMacro(group.calories)}</span>
                                kcal
                              </span>
                              <span>
                                <span className="block font-semibold text-slate-800">{formatMacro(group.protein)}g</span>
                                protein
                              </span>
                              <span>
                                <span className="block font-semibold text-slate-800">{formatMacro(group.carbs)}g</span>
                                carbs
                              </span>
                              <span>
                                <span className="block font-semibold text-slate-800">{formatMacro(group.fat)}g</span>
                                fat
                              </span>
                            </span>
                            {isSelectingChoice ? (
                              <span className="mt-2 text-xs font-semibold text-cyan-700">
                                {isChoiceSelected ? 'Clearing...' : 'Selecting...'}
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                <div className="flex flex-wrap items-stretch gap-3">
                  {displaySuggestions.map((suggestion) => {
                    const cardSelectionKey = `${activeDay.day}-${mealName}-${cleanString(suggestion.optionGroupId) || 'single'}-${suggestion.id}`;
                    const isSelecting = selectingKey === cardSelectionKey;
                    const isCardSelectable = Boolean(suggestion.alternate && !suggestion.isSelected && !isGenericMealSlot);
                    const isCardClearable = Boolean(suggestion.isSelected && !isGenericMealSlot);
                    const isCardInteractive = isCardSelectable || isCardClearable;
                    const generatedLabel = getGeneratedIndicatorLabel(suggestion);
                    const visibleSourceLabel = getVisibleRecipeSourceLabel(suggestion.source);
                    const visibleDietLabels = firstNonEmptyTags(suggestion.dietLabels);

                    const handleSelect = async () => {
                      if (!suggestion.alternate || isSelecting) return;
                      setSelectingKey(cardSelectionKey);
                      try {
                        await onSelectAlternateRecipe(
                          activeDay.day,
                          mealName,
                          mealOrder,
                          suggestion.alternate
                        );
                      } finally {
                        setSelectingKey(null);
                      }
                    };

                    const handleClear = async () => {
                      if (isSelecting) return;
                      setSelectingKey(cardSelectionKey);
                      try {
                        await onClearMealSelection(activeDay.day, mealName, mealOrder);
                      } finally {
                        setSelectingKey(null);
                      }
                    };

                    const handleCardAction = async () => {
                      if (isCardClearable) {
                        await handleClear();
                      } else if (isCardSelectable) {
                        await handleSelect();
                      }
                    };

                    return (
                      <div
                        key={cardSelectionKey}
                        className={`relative flex w-full max-w-[355px] flex-col rounded-[12.88px] border-[1.84px] bg-white p-[16.55px] transition-colors ${
                          suggestion.isSelected
                            ? 'border-cyan-400 shadow-[0_0_0_1px_rgba(34,211,238,0.2)]'
                            : 'border-slate-200 hover:border-cyan-300'
                        } ${isCardInteractive ? 'cursor-pointer' : ''}`}
                        role={isCardInteractive ? 'button' : undefined}
                        tabIndex={isCardInteractive ? 0 : -1}
                        onClick={
                          isCardInteractive
                            ? () => {
                                void handleCardAction();
                              }
                            : undefined
                        }
                        onKeyDown={
                          isCardInteractive
                            ? (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  void handleCardAction();
                                }
                              }
                            : undefined
                        }
                      >
                        <button
                          type="button"
                          className={`absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded border ${
                            suggestion.isSelected
                              ? 'border-cyan-500 bg-cyan-500 text-white hover:border-cyan-600 hover:bg-cyan-600'
                              : 'border-slate-300 bg-white text-transparent hover:border-cyan-400'
                          }`}
                          aria-label={suggestion.isSelected ? 'Deselect meal' : 'Select meal'}
                          disabled={isGenericMealSlot || isSelecting || (!isCardSelectable && !isCardClearable)}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (isGenericMealSlot) return;
                            void handleCardAction();
                          }}
                        >
                          <Check className="h-3 w-3" />
                        </button>

                        <div className="flex items-start">
                          <div className="min-w-0 pr-7 pt-0.5">
                            <p className="line-clamp-2 min-h-8 text-[13px] font-medium leading-4 text-slate-800">
                              {suggestion.title}
                            </p>
                            {visibleSourceLabel ? (
                              <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                                <Clock3 className="h-3 w-3" />
                                {visibleSourceLabel}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <p className="mt-3 line-clamp-2 min-h-[30px] text-xs leading-4 text-slate-500">
                          {suggestion.description}
                        </p>

                        <div className="mt-2 border-b border-slate-200 pb-2">
                          <div className="flex min-h-7 items-center justify-between gap-3">
                            <p className="text-lg font-semibold text-cyan-600">{formatMacro(suggestion.calories)} kcal</p>
                            {generatedLabel ? (
                              <Tag variant="outlined" size="xs" className="shrink-0 bg-cyan-50 uppercase text-cyan-700">
                                {generatedLabel}
                              </Tag>
                            ) : null}
                          </div>

                          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-200 pt-2 text-xs sm:grid-cols-4">
                            {[
                              ['Protein', suggestion.protein],
                              ['Carbs', suggestion.carbs],
                              ['Fat', suggestion.fat],
                              ['Fiber', suggestion.fiber],
                            ].map(([label, value]) => (
                              <span key={label} className="min-w-0 text-center">
                                <span className="block leading-4 text-slate-500">{label}</span>
                                <span className="block whitespace-nowrap font-medium leading-4 tabular-nums text-slate-700">
                                  {formatMacro(value)}g
                                </span>
                              </span>
                            ))}
                          </div>
                        </div>
                        {visibleDietLabels.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1 text-xs">
                            {visibleDietLabels.slice(0, 3).map((label, idx) => (
                              <Tag key={idx} variant="neutral" size="xs" className="rounded-sm px-1.5 py-0.5 font-medium text-gray-600">
                                {getDietaryDisplayName(label, 'dietLabels')}
                              </Tag>
                            ))}
                          </div>
                        )}

                        <SuggestionPreferenceDetails
                          cuisines={suggestion.cuisines}
                          cuisineTypes={suggestion.cuisineTypes}
                          allergens={suggestion.allergens}
                        />

                        <div className="mt-auto grid grid-cols-1 gap-2 pt-3 sm:grid-cols-2">
                          <Button
                            data-demo-id="ai-plan-draft-smart-edit"
                            type="button"
                            size="sm"
                            className="h-11 rounded-[14px] bg-cyan-500 text-xs text-white hover:bg-cyan-600 hover:text-white"
                            onClick={(event) => {
                              event.stopPropagation();
                              onViewDetails(
                                suggestion.id,
                                suggestion.title,
                                activeDay.day,
                                mealName,
                                suggestion.source,
                                mealOrder
                              );
                            }}
                          >
                            View Recipe
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-11 rounded-[14px] border-slate-300 bg-white text-xs text-slate-900 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-100 disabled:border-slate-300 disabled:bg-white disabled:text-slate-400 disabled:hover:border-slate-300 disabled:hover:bg-white"
                            disabled={smartEditDisabled}
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenSmartEdit({
                                day: activeDay.day,
                                mealName,
                                mealOrder,
                                currentRecipeId: suggestion.id,
                                recipeId: suggestion.id,
                                recipeName: suggestion.title,
                                recipeImage: suggestion.image,
                                source: suggestion.source ?? '',
                                calories: suggestion.calories,
                                protein: suggestion.protein,
                                carbs: suggestion.carbs,
                                fat: suggestion.fat,
                                fiber: suggestion.fiber,
                                alternateRecipe: suggestion.alternate,
                              });
                            }}
                          >
                            Smart Edit
                            <Sparkles className="ml-1 h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
              )}
            </SortableMealSection>
          );
            })}
          </div>
        </SortableContext>
      </DndContext>

      <Dialog open={applyAllDialogOpen} onOpenChange={setApplyAllDialogOpen}>
        <DialogContent className="w-[92vw] max-w-[450px] gap-0 overflow-hidden rounded-[20px] border border-slate-200 p-0 shadow-xl [&>button]:hidden">
          <DialogTitle className="sr-only">Apply to All Days</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm copying meals from the current day to every other day in this meal plan.
          </DialogDescription>
          <div className="border-b border-slate-200 px-5 py-4 pr-14">
            <button
              type="button"
              className="absolute right-5 top-4 text-slate-400 transition-colors hover:text-slate-600"
              onClick={() => setApplyAllDialogOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-slate-900" />
              <h3 className="text-[27px] font-semibold leading-none text-slate-800">Apply to All Days?</h3>
            </div>
          </div>

          <div className="space-y-3 px-5 py-4">
            <p className="text-base text-slate-600">
              This will copy the meals from <span className="font-semibold text-slate-800">Day {activeDay.day}</span> to all{' '}
              <span className="font-semibold text-slate-800">{sortedDays.length} days</span> in this meal plan.
            </p>

            <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="flex items-start gap-1.5 text-sm text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <span>
                This will replace all existing meal selections on other days.
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2.5 px-5 pb-4">
            <Button
              type="button"
              variant="secondary"
              className="h-10 min-w-[136px] rounded-xl bg-slate-200 text-[22px] font-medium text-slate-700 hover:bg-slate-300"
              onClick={() => setApplyAllDialogOpen(false)}
              disabled={applyingToAllDays}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-10 min-w-[176px] rounded-xl bg-cyan-500 text-[19px] font-medium text-white hover:bg-cyan-600"
              onClick={handleApplyToAllDays}
              disabled={applyingToAllDays}
            >
              {applyingToAllDays ? 'Applying...' : 'Apply to All Days'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CopyDayModal
        open={copyModalOpen}
        onOpenChange={setCopyModalOpen}
        fromDay={activeDay.day}
        availableDays={availableDaysForCopy}
        onCopyDays={handleCopyExistingDays}
      />
    </div>
  );
};

export default MealPlanDaysTabs;
